/**
 * Admin Page (VH-19: RBAC + existing settings)
 *
 * Tabbed admin interface with three sections:
 * - Settings: a note that self-registration is gone (VH-285)
 * - Users: User management with role changes
 * - Coach Assignments: Coach-to-user assignment management
 *
 * Access restricted to super_user role.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  AcademicCapIcon,
  CameraIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../hooks/useAuth'
import UserManagement from '../components/admin/UserManagement'
import CoachAssignments from '../components/admin/CoachAssignments'
import AIModelSettings from '../components/admin/AIModelSettings'
import LLMSettings from '../components/admin/LLMSettings'
import ModelComparison from '../components/admin/ModelComparison'

const TABS = [
  { id: 'settings', icon: Cog6ToothIcon, labelKey: 'admin.tabs.settings' },
  { id: 'users', icon: UsersIcon, labelKey: 'admin.tabs.users' },
  { id: 'coachAssignments', icon: AcademicCapIcon, labelKey: 'admin.tabs.coachAssignments' },
  { id: 'llmSettings', icon: CameraIcon, labelKey: 'admin.tabs.llmSettings' },
  // VH-280: results of the model comparison runs and the blind review.
  { id: 'modelComparison', icon: ScaleIcon, labelKey: 'admin.tabs.modelComparison' },
]


export default function Admin() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('settings')

  // VH-19: Use role-based check instead of hardcoded email
  const isSuperUser = user?.role === 'super_user'

  // Access denied for non-super-users
  if (!isSuperUser) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  {t('admin.accessDenied', 'You do not have permission to access this page.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderSettingsTab = () => (
    <div className="space-y-6">
      {/* VH-285: self-registration is gone; nobody signs up without an authenticator. */}
      <div className="bg-surface-container shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t('admin.userRegistration')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{t('admin.noSelfRegistration')}</p>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'settings':
        return renderSettingsTab()
      case 'users':
        return (
          <div className="bg-surface-container shadow rounded-lg overflow-hidden px-4 py-5 sm:p-6">
            <UserManagement />
          </div>
        )
      case 'coachAssignments':
        return (
          <div className="bg-surface-container shadow rounded-lg overflow-hidden px-4 py-5 sm:p-6">
            <CoachAssignments />
          </div>
        )
      case 'llmSettings':
        return (
          <div className="bg-surface-container shadow rounded-lg overflow-hidden px-4 py-5 sm:p-6">
            <AIModelSettings />
            <LLMSettings />
          </div>
        )
      case 'modelComparison':
        return (
          <div className="bg-surface-container shadow rounded-lg overflow-hidden px-4 py-5 sm:p-6">
            <ModelComparison />
          </div>
        )
      default:
        return renderSettingsTab()
    }
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate flex items-center gap-3">
              <Cog6ToothIcon className="h-8 w-8 text-gray-600" />
              {t('admin.title')}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {t('admin.subtitle')}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                    ${isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className={`h-5 w-5 mr-2 ${isActive ? 'text-primary-500' : 'text-gray-400'}`} />
                  {t(tab.labelKey)}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab content */}
        {renderTabContent()}
      </div>
    </div>
  )
}
